import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

type LeaveAction = () => void | Promise<void>;

/**
 * Holds in-app navigation (links, menu clicks, `navigate`) while `shouldBlock` is true. The browser
 * Back button is not held because this router version has no hook for it, and closing the tab only
 * gets the browser's own dialog.
 *
 * @param shouldBlock read at the moment of leaving, so it always sees the latest form
 * @returns whether a leave is held, and the functions to continue or drop it
 */
export const useLeaveGuard = (shouldBlock: () => boolean) => {
    const { navigator } = useContext(NavigationContext);
    const [pendingLeave, setPendingLeave] = useState<LeaveAction | null>(null);

    const shouldBlockRef = useRef(shouldBlock);
    shouldBlockRef.current = shouldBlock;
    // Set while the page itself is leaving, so its own navigation is not held again
    const allowLeaveRef = useRef(false);

    useEffect(() => {
        const push = navigator.push;
        navigator.push = (...args: Parameters<typeof push>) => {
            if (allowLeaveRef.current || !shouldBlockRef.current()) {
                push(...args);
                return;
            }
            setPendingLeave(() => () => push(...args));
        };
        return () => {
            navigator.push = push;
        };
    }, [navigator]);

    const leaveWithoutAsking = useCallback(async (action: LeaveAction) => {
        allowLeaveRef.current = true;
        try {
            await action();
        } finally {
            allowLeaveRef.current = false;
        }
    }, []);

    const leaveWith = useCallback(
        (action: LeaveAction) => {
            if (shouldBlockRef.current()) {
                setPendingLeave(() => action);
                return;
            }
            void leaveWithoutAsking(action);
        },
        [leaveWithoutAsking]
    );

    const proceed = useCallback(async () => {
        const leave = pendingLeave;
        setPendingLeave(null);
        if (leave) {
            await leaveWithoutAsking(leave);
        }
    }, [pendingLeave, leaveWithoutAsking]);

    const cancel = useCallback(() => setPendingLeave(null), []);

    return { isLeaving: pendingLeave !== null, leaveWith, leaveWithoutAsking, proceed, cancel };
};
